import { installNativePinch } from "./native-pinch";
import { installNativeToolbarEntrance } from "./native-toolbar-entrance";
import { NativeSelectionProperties } from "./native-selection-properties";
import {
  chooseSaveDestination,
  fileNameWithExtension,
} from "./native-save-destination";
import { ENGINE_EXTENSION } from "../document/engine-format";
import { NativeZoomControls } from "./native-zoom";
import { textCacheStats } from "../render/text-cache";
import { textLayoutStats } from "../render/native-text-layout";
import { NativePreview } from "./native-preview";
import { NativeInteractive } from "./native-interactive";
import { NativeScreens } from "./native-screens";
import { activePage } from "../native/pages";
import { NativePublication } from "./native-publication";
import { NativeTemplates } from "./native-templates";
import { NativeTemplateSave } from "./native-template-save";
import { decodeStoredDocument } from "../storage/project-record";
import { assertNativeDocument } from "../native/document";
import { isRecord } from "../document/model";
import { prepareLegacyDocument } from "./native-legacy-import";
import { sampleOriginalImage } from "../render/native-image-sampling";
import { NativeColorPicker } from "./native-color-picker";
import { NativeObjectTrash } from "./native-object-trash";
import { NativeEffectsEditor } from "./native-effects";
import { NativeDeformation } from "./native-deformation";
import { NativeMerge } from "./native-merge";
import { selectionEntities } from "../native/blocks";
import { NativeBlocks } from "./native-blocks";
import { NativeImageLibrary } from "./native-image-library";
import { importNativeImage, importNativeImages } from "./native-image-import";
import { installNativeImageDrop } from "./native-image-drop";
import { NativeWorkbench } from "./native-workbench";
import { NativeFiles, prepareFonts } from "./native-files";
import { NativeRasterCache } from "../render/native-raster-cache";
import { NativeScenePainter } from "../render/native-scene-painter";
import { setPixelHitTest } from "../native/geometry";
import "./native-transferred.css";
import "./native-workbench.css";
import { NativeCrop } from "./native-crop";
import { NativePageInspector } from "./native-page-inspector";
import { NativeGrid } from "./native-grid";
import { NativeClipboard } from "./native-clipboard";
import "./native.css";
import "./native-dock.css";
import { updateDockContrast } from "./native-dock-contrast";
import "./native-viewport.css";
import { installNativeViewport } from "./native-viewport";
import { NativeSession } from "../native/session";
import { NativePointer } from "../native/pointer";
import { bounds, type Camera } from "../native/geometry";
import { NativeInspector } from "./native-inspector";
import { NativeLayers } from "./native-layers";
import { NativeRanges } from "./native-ranges";
import { NativeShapeTools } from "./native-shape-tools";
import { NativeChrome } from "./native-chrome";
import { installNativeIcons } from "./native-icons";
import { saveNativeArchive, openNativeArchive } from "../native/archive";
import { ImageStore } from "../render/image-store";
import { NativeWebGLRenderer } from "../render/native-webgl";
import { NativeDisplayCanvas } from "../render/native-display-canvas";
import { NativeCanvasRenderer } from "../render/native-canvas";
import { drawSelection } from "../render/native-overlay";
import { PageBoundary } from "../native/page-boundary";
import { drawPageBoundary } from "../render/native-page-boundary";
import type { NativeRenderer } from "../render/native-port";
import type { ZipProvider } from "../adapters/jszip-project";
import { transformNodes, nudgeCommandForKey } from "../commands/node-transform";

await document.fonts.load('40px "3DC Tall Display"');
const root = document.getElementById("native-editor")!;
installNativeViewport(root);
root.addEventListener("click", (event) => {
  if (event.detail > 0) (event.target as HTMLElement).closest("button")?.blur();
});
const byId = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T;
const session = new NativeSession(
    root.dataset.mode === "interactive" ? "interactive" : "graphics",
  ),
  images = new ImageStore(schedule);
const stage = byId<HTMLElement>("native-stage"),
  overlay = byId<HTMLCanvasElement>("native-overlay");
const camera: Camera = { x: 0, y: 0, zoom: 1 };
const scenePainter = new NativeScenePainter();
let fontRevision = 0;
let surface: HTMLCanvasElement, renderer: NativeRenderer;
let frameRequested = false,
  operation = 0;
function makeRenderer(forceCanvas = false) {
  renderer?.dispose();
  surface?.remove();
  surface = document.createElement("canvas");
  surface.id = "native-surface";
  surface.setAttribute("aria-hidden", "true");
  stage.prepend(surface);
  try {
    if (forceCanvas) throw new Error("fallback");
    renderer = new NativeWebGLRenderer(surface, images);
    surface.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      makeRenderer(true);
      schedule();
    });
  } catch {
    surface.remove();
    surface = document.createElement("canvas");
    surface.id = "native-surface";
    surface.setAttribute("aria-hidden", "true");
    stage.prepend(surface);
    renderer = new NativeDisplayCanvas(surface, images);
  }
  root.dataset.renderer = renderer.kind;
}
makeRenderer(
  new URLSearchParams(location.search).get("renderer") === "canvas2d",
);
function schedule() {
  if (!frameRequested) {
    frameRequested = true;
    requestAnimationFrame(paint);
  }
}
const grid = new NativeGrid(schedule, () => {
  pointer.cancel();
  session.select([]);
});
const pointer = new NativePointer(
  overlay,
  session,
  camera,
  schedule,
  grid.settings,
);
installNativeIcons(root);
const selectionProperties = new NativeSelectionProperties();
const chrome = new NativeChrome(
  root,
  () => {
    pointer.cancel();
    layers.cancel();
    ranges.cancel();
    pointer.space = false;
    schedule();
  },
  schedule,
  () => session.page.workspaceColor,
);
pointer.wheelEnabled = (e) =>
  !chrome.modal && (chrome.wheelZoomMode === "wheel" || e.altKey);
installNativePinch(
  root,
  overlay,
  camera,
  () => chrome.modal || session.mode !== "graphics",
  schedule,
);
const layers = new NativeLayers(session, pointer, schedule, message);
const shapeTools = new NativeShapeTools(
  root,
  (tool) => pointer.setTool(tool),
  () => chrome.closePopup(),
);
const inspector = new NativeInspector(session, pointer, schedule, message);
const ranges = new NativeRanges(session, pointer, schedule, message);
const crop = new NativeCrop(
  overlay,
  session,
  pointer,
  camera,
  images,
  grid.settings,
  schedule,
  message,
  () => chrome.modal,
  () => chrome.workArea(),
);
const pageInspector = new NativePageInspector(
  session,
  () => {
    pointer.setTool("select");
    layers.cancel();
    ranges.cancel();
    session.select([]);
    if (
      document
        .getElementById("native-toggle-right")!
        .getAttribute("aria-expanded") === "false"
    )
      document.getElementById("native-toggle-right")!.click();
  },
  schedule,
  message,
  () =>
    pointer.active ||
    layers.interacting ||
    ranges.active ||
    blocks.active ||
    chrome.modal ||
    effectEditor.active ||
    deformation.active ||
    merge.busy ||
    crop.gesturing ||
    crop.busy,
  camera,
  crop,
);
pageInspector.onWorkspaceApply = () => chrome.clearWorkspaceOverride();
const zoomControls = new NativeZoomControls(
  camera,
  () => chrome.workArea(),
  () => session.page.size,
  () =>
    chrome.modal ||
    pointer.active ||
    workbench.active ||
    crop.gesturing ||
    deformation.gesturing,
  () => chrome.wheelZoomMode,
  schedule,
);
const workbench = new NativeWorkbench(
  session,
  pointer,
  overlay,
  camera,
  images,
  crop,
  schedule,
  () => chrome.modal,
  message,
);
layers.preserveAreaTool = () => !!workbench.tool?.endsWith("-area");
const hitCache = new NativeRasterCache(images);
setPixelHitTest((node, p) => hitCache.hit(session.document, node, p));
const clipboard = new NativeClipboard(
  session,
  images,
  () =>
    chrome.modal ||
    effectEditor.active ||
    deformation.active ||
    merge.busy ||
    pointer.active ||
    layers.interacting ||
    ranges.active ||
    blocks.active ||
    shapeTools.opened ||
    pageInspector.dirty ||
    crop.active ||
    workbench.active ||
    workbench.busy ||
    workbench.text.editing ||
    workbench.groups.interacting,
  () => {
    const area = chrome.workArea();
    return {
      x: (area.x + area.width / 2 - camera.x) / camera.zoom,
      y: (area.y + area.height / 2 - camera.y) / camera.zoom,
    };
  },
  () => {
    pointer.setTool("select");
    chrome.closePopup();
    schedule();
  },
  message,
);
function prepareAdvanced() {
  if (
    pointer.active ||
    session.gesturing ||
    pageInspector.dirty ||
    crop.active ||
    crop.busy ||
    workbench.active ||
    workbench.busy ||
    chrome.modal
  )
    return false;
  workbench.finish();
  return true;
}
function sampleColor(x: number, y: number) {
  const canvas = document.createElement("canvas"),
    r = new NativeCanvasRenderer(canvas, images);
  r.draw({
    document: session.document,
    camera: { x: camera.x - x, y: camera.y - y, zoom: camera.zoom },
    width: 1,
    height: 1,
    dpr: 1,
  });
  const p = canvas.getContext("2d")!.getImageData(0, 0, 1, 1).data;
  r.dispose();
  return (
    "#" +
    [p[0]!, p[1]!, p[2]!].map((v) => v.toString(16).padStart(2, "0")).join("")
  );
}
const effectEditor = new NativeEffectsEditor(
  session,
  schedule,
  prepareAdvanced,
  (x, y) =>
    session.selected
      ? sampleOriginalImage(images, session.selected, session.document.assets, {
          x: (x - camera.x) / camera.zoom,
          y: (y - camera.y) / camera.zoom,
        })
      : null,
);
const palette = new NativeColorPicker(session, schedule, sampleColor);
new NativeObjectTrash(root, session, pointer, schedule);
const deformation = new NativeDeformation(
  session,
  pointer,
  overlay,
  camera,
  schedule,
  prepareAdvanced,
);
const merge = new NativeMerge(
  session,
  images,
  schedule,
  prepareAdvanced,
  message,
);
const files = new NativeFiles(
  session,
  images,
  () =>
    pointer.active ||
    ranges.active ||
    blocks.active ||
    layers.interacting ||
    workbench.active ||
    workbench.busy ||
    workbench.text.previewing ||
    pageInspector.dirty ||
    crop.busy ||
    crop.gesturing ||
    chrome.modal ||
    session.gesturing ||
    effectEditor.active ||
    deformation.active ||
    merge.busy,
  () => workbench.finish(),
  schedule,
  fit,
  message,
);
const prepareInteractive = () => {
  if (
    !prepareAdvanced() ||
    effectEditor.active ||
    deformation.active ||
    merge.busy ||
    layers.interacting ||
    ranges.active
  )
    return false;
  workbench.finish();
  pointer.setTool("select");
  chrome.closePopup();
  return true;
};
const interactive =
  session.mode === "interactive"
    ? new NativeInteractive(
        session,
        images,
        prepareInteractive,
        schedule,
        message,
      )
    : null;
if (session.mode === "interactive")
  new NativePreview(session, prepareInteractive, message);
new NativePublication(files, session, prepareInteractive, message);
const screens =
  session.mode === "interactive"
    ? new NativeScreens(
        session,
        (action) => {
          if (
            pointer.active ||
            session.gesturing ||
            effectEditor.active ||
            deformation.active ||
            merge.busy ||
            crop.busy ||
            workbench.busy ||
            layers.interacting ||
            ranges.active ||
            blocks.active ||
            chrome.modal
          )
            return;
          pageInspector.requestLeave(() => {
            workbench.finish();
            workbench.activate(null);
            pointer.cancel();
            pointer.setTool("select");
            layers.cancel();
            crop.close();
            chrome.closePopup();
            action();
            fit();
          });
        },
        schedule,
        message,
      )
    : null;
clipboard.prepareCopy = () => workbench.text.cancelFontPreview();
workbench.regions.copied = (document, ids) =>
  clipboard.setContents(document, ids);
function fit() {
  const area = chrome.workArea(),
    page = session.page.size;
  camera.zoom = Math.max(
    0.1,
    Math.min(1, area.width / page.width, area.height / page.height),
  );
  camera.x = area.x + (area.width - page.width * camera.zoom) / 2;
  camera.y = area.y + (area.height - page.height * camera.zoom) / 2;
  schedule();
}
let statusTimer: ReturnType<typeof setTimeout> | undefined;
function message(text: string, error = false) {
  const output = byId<HTMLElement>("native-status");
  output.textContent = text;
  output.dataset.error = String(error);
  output.dataset.faded = "false";
  clearTimeout(statusTimer);
  statusTimer = setTimeout(() => {
    output.dataset.faded = "true";
  }, 5000);
}
const pageBoundary = new PageBoundary();
function paint() {
  frameRequested = false;
  hitCache.prune(new Set(session.page.nodes.map((n) => n.id)));
  const { width, height } = stage.getBoundingClientRect(),
    dpr = window.devicePixelRatio || 1;
  const previewDocument = effectEditor.previewDocument;
  const workspaceColor =
    pageInspector.previewColor !== session.page.workspaceColor
      ? pageInspector.previewColor
      : (chrome.workspaceColor ?? session.page.workspaceColor);
  updateDockContrast(root, workspaceColor);
  const renderDocument =
    workspaceColor === session.page.workspaceColor
      ? previewDocument
      : {
          ...previewDocument,
          pages: [
            {
              ...activePage(previewDocument),
              workspaceColor,
            },
          ],
        };
  scenePainter.draw(
    renderer,
    {
      document: renderDocument,
      camera,
      width,
      height,
      dpr,
      workspaceGrid: chrome.workspaceGrid,
    },
    images.revision,
    fontRevision,
  );
  drawSelection(
    overlay,
    selectionEntities(session.selectedNodes),
    workbench.text.frameEditing || deformation.active ? null : pointer.frame,
    pointer.marquee,
    camera,
    width,
    height,
    dpr,
    (ctx) => {
      grid.draw(ctx, camera, session.page.size, width, height);
      const page = activePage(renderDocument);
      drawPageBoundary(
        ctx,
        pageBoundary.coverage(page.nodes, page.size.width, page.size.height),
        camera,
      );
    },
  );
  byId<HTMLButtonElement>("native-undo").disabled =
    session.history.length < 2 ||
    pointer.active ||
    crop.busy ||
    crop.gesturing ||
    workbench.active ||
    workbench.busy ||
    workbench.groups.interacting;
  byId<HTMLButtonElement>("native-redo").hidden = !session.history.canRedo;
  byId<HTMLButtonElement>("native-redo").disabled =
    !session.history.canRedo ||
    pointer.active ||
    crop.busy ||
    crop.gesturing ||
    workbench.active ||
    workbench.busy ||
    workbench.groups.interacting;
  byId<HTMLButtonElement>("native-delete").disabled =
    !session.selectedNodes.length || pointer.active;
  byId<HTMLElement>("native-select").setAttribute(
    "aria-pressed",
    String(pointer.tool === "select"),
  );
  shapeTools.render(pointer.tool);
  overlay.style.cursor = pointer.space ? "grab" : pointer.cursor;
  zoomControls.render();
  byId<HTMLElement>("native-save-state").textContent = session.dirty
    ? "Есть изменения"
    : "Без изменений";
  inspector.render();
  ranges.render();
  layers.render();
  chrome.render(session.selectedNodes);
  pageInspector.render();
  crop.render(overlay.getContext("2d")!);
  workbench.render(overlay.getContext("2d")!);
  effectEditor.render();
  palette.render();
  deformation.render(overlay.getContext("2d")!);
  merge.render();
  chrome.renderIcons();
  templateSave.render();
  interactive?.render();
  screens?.render();
  blocks.render();
  selectionProperties.render(session.selectedNodes);
  files.observe();
  byId<HTMLElement>("native-empty").hidden = session.page.nodes.length > 0;
  byId<HTMLElement>("native-empty").style.left =
    camera.x + (session.page.size.width * camera.zoom) / 2 + "px";
  byId<HTMLElement>("native-empty").style.top =
    camera.y + (session.page.size.height * camera.zoom) / 2 + "px";
}
byId<HTMLElement>("native-select").onclick = () => pointer.setTool("select");
function undo(redo = false) {
  const before = session.page.size;
  session.undo(redo);
  if (
    before.width !== session.page.size.width ||
    before.height !== session.page.size.height
  ) {
    const a = chrome.workArea();
    camera.x = a.x + (a.width - session.page.size.width * camera.zoom) / 2;
    camera.y = a.y + (a.height - session.page.size.height * camera.zoom) / 2;
  }
}
byId<HTMLElement>("native-undo").onclick = () => {
  undo();
  schedule();
};
byId<HTMLElement>("native-redo").onclick = () => {
  undo(true);
  schedule();
};
byId<HTMLElement>("native-delete").onclick = () => {
  session.removeSelected();
  schedule();
};
const Zip = (window as unknown as { JSZip: ZipProvider }).JSZip;
let savingProject = false;
let projectFileName = "Мой проект.3dcd";
async function saveCurrentProject(): Promise<boolean> {
  if (savingProject) return false;
  workbench.finish();
  files.closeMenu();
  if (
    pointer.active ||
    layers.dragging ||
    ranges.active ||
    blocks.active ||
    pageInspector.dirty ||
    chrome.modal ||
    effectEditor.active ||
    deformation.active ||
    merge.busy ||
    crop.busy ||
    crop.gesturing ||
    workbench.active ||
    workbench.busy
  )
    return false;
  const snapshot = structuredClone(session.document),
    revision = session.revision,
    generation = session.generation;
  savingProject = true;
  try {
    const destination = await chooseSaveDestination(
      projectFileName,
      ENGINE_EXTENSION,
      "application/zip",
    );
    if (!destination) {
      message("Сохранение отменено.");
      return false;
    }
    const blob = await saveNativeArchive(snapshot, Zip);
    await destination.write(blob);
    if (session.generation === generation) projectFileName = destination.name;
    if (
      session.revision === revision &&
      session.generation === generation &&
      !pointer.active &&
      !ranges.active
    )
      session.dirty = false;
    message(
      session.dirty
        ? "Файл сохранён. Более поздние изменения ещё не сохранены."
        : "Проект сохранён в .3dcd.",
    );
    schedule();
    return session.revision === revision && session.generation === generation;
  } catch (error) {
    message((error as Error).message, true);
    return false;
  } finally {
    savingProject = false;
  }
}
byId<HTMLElement>("native-save").onclick = () => {
  void saveCurrentProject();
};
async function loadFile(file: File, kind: "image" | "project") {
  workbench.finish();
  pointer.cancel();
  layers.cancel();
  ranges.cancel();
  const token = ++operation,
    generation = session.generation,
    revision = session.revision;
  message("Читаем файл…");
  let importedLegacy = false;
  try {
    if (kind === "project") {
      const document = await openNativeArchive(file, Zip, (project) => {
        importedLegacy = true;
        return prepareLegacyDocument(project, images);
      });
      await images.prepareDisplay(document.assets);
      await prepareFonts(document);
      if (token !== operation) return;
      if (
        chrome.modal ||
        effectEditor.active ||
        deformation.active ||
        merge.busy ||
        pageInspector.dirty ||
        crop.busy ||
        crop.gesturing ||
        workbench.active ||
        workbench.busy
      )
        throw new Error("Закройте настройки и откройте проект ещё раз.");
      if (
        generation !== session.generation ||
        revision !== session.revision ||
        pointer.active ||
        layers.interacting ||
        ranges.active ||
        blocks.active
      )
        throw new Error(
          "За время чтения вы изменили документ или начали другое действие. Повторите открытие файла.",
        );
      session.reset(document);
      images.retain(document.assets);
      if (importedLegacy) session.dirty = true;
      projectFileName = fileNameWithExtension(file.name, ENGINE_EXTENSION);
      pointer.setTool("select");
      fit();
    } else {
      const area = chrome.workArea();
      await importNativeImage(
        file,
        undefined,
        session,
        images,
        {
          x: (area.x + area.width / 2 - camera.x) / camera.zoom,
          y: (area.y + area.height / 2 - camera.y) / camera.zoom,
        },
        () => token === operation && !chrome.modal,
      );
      pointer.setTool("select");
    }
    message(
      kind === "project"
        ? importedLegacy
          ? "Старый проект импортирован. Сохранение — .3dcd." +
            (session.document.pages.some((p) =>
              p.nodes.some(
                (n) =>
                  Array.isArray(n.layer.legacyGroupPath) &&
                  n.layer.legacyGroupPath.length > 1,
              ),
            )
              ? " Объекты вложенных групп сохранены в общей группе."
              : "")
          : "Проект открыт."
        : "Изображение добавлено.",
    );
    schedule();
  } catch (error) {
    if (token === operation) message((error as Error).message, true);
  }
}
for (const kind of ["image", "project"] as const) {
  const input = byId<HTMLInputElement>("native-" + kind + "-file");
  byId<HTMLElement>("native-" + kind).onclick = () => {
    if (
      kind === "project" &&
      session.dirty &&
      !confirm(
        "Открыть другой проект? Сначала скачайте текущий, если хотите сохранить изменения.",
      )
    )
      return;
    input.click();
  };
  input.onchange = () => {
    const file = input.files?.[0];
    input.value = "";
    if (file) void loadFile(file, kind);
  };
}
const templates = new NativeTemplates({
  api: (path) => files.api(path),
  sharedOpened: (template) => templateSave.opened(template),
  request: (action) => pageInspector.requestLeave(action),
  prepare: () => {
    if (
      pageInspector.dirty ||
      chrome.modal ||
      pointer.active ||
      layers.interacting ||
      ranges.active ||
      blocks.active ||
      effectEditor.active ||
      deformation.active ||
      merge.busy ||
      crop.busy ||
      crop.gesturing ||
      workbench.busy
    )
      return null;
    workbench.finish();
    chrome.closePopup();
    files.closeMenu();
    const token = ++operation,
      generation = session.generation,
      revision = session.revision,
      selection = JSON.stringify(session.selectedIds),
      tool = pointer.tool;
    return () =>
      token === operation &&
      generation === session.generation &&
      revision === session.revision &&
      selection === JSON.stringify(session.selectedIds) &&
      tool === pointer.tool &&
      !chrome.modal &&
      !pageInspector.dirty &&
      !pointer.active &&
      !layers.interacting &&
      !ranges.active &&
      !blocks.active &&
      !effectEditor.active &&
      !deformation.active &&
      !merge.busy &&
      !crop.busy &&
      !crop.gesturing &&
      !workbench.active &&
      !workbench.busy;
  },
  read: async (project) => {
    const native =
      isRecord(project) && project.format === "creator-engine-pro-document";
    const scene = native
      ? decodeStoredDocument(project)
      : await prepareLegacyDocument(project, images);
    if (native) {
      assertNativeDocument(scene);
      scene.extensions = {
        ...scene.extensions,
        projectId: crypto.randomUUID(),
      };
      await images.prepareDisplay(scene.assets);
    }
    await prepareFonts(scene);
    return scene;
  },
  occupied: () => session.page.nodes.length > 0 || session.dirty,
  save: saveCurrentProject,
  commit: (scene) => {
    crop.close();
    session.reset(scene);
    images.retain(scene.assets);
    session.dirty = true;
    pointer.setTool("select");
    fit();
    byId<HTMLElement>("native-page-open").click();
    schedule();
  },
  message,
});
const templateSave = new NativeTemplateSave(
  files,
  session,
  prepareInteractive,
  () => templates.refreshShared(),
  message,
);
const dropBlocked = () =>
  root.inert ||
  chrome.modal ||
  effectEditor.active ||
  deformation.active ||
  merge.busy ||
  pointer.active ||
  session.gesturing ||
  layers.interacting ||
  ranges.active ||
  blocks.active ||
  pageInspector.dirty ||
  crop.active ||
  workbench.active ||
  workbench.busy ||
  workbench.text.editing ||
  workbench.groups.interacting;
installNativeImageDrop(
  overlay,
  camera,
  (files, point) => {
    const token = ++operation,
      selection = JSON.stringify(session.selectedIds);
    message("Добавляем изображения…");
    void importNativeImages(
      files.map((file) => ({ file })),
      session,
      images,
      point,
      () =>
        token === operation &&
        !dropBlocked() &&
        selection === JSON.stringify(session.selectedIds),
    )
      .then(() => {
        pointer.setTool("select");
        chrome.closePopup();
        message(
          files.length === 1
            ? "Изображение добавлено."
            : `Добавлено изображений: ${files.length}.`,
        );
        schedule();
      })
      .catch((error) => {
        if (token === operation) message((error as Error).message, true);
      });
  },
  dropBlocked,
  message,
);
const imageLibrary = new NativeImageLibrary(
  async (entries, current = () => true) => {
    const area = chrome.workArea();
    await importNativeImages(
      entries,
      session,
      images,
      {
        x: (area.x + area.width / 2 - camera.x) / camera.zoom,
        y: (area.y + area.height / 2 - camera.y) / camera.zoom,
      },
      () => {
        if (
          document.querySelector("dialog[open]") ||
          pageInspector.dirty ||
          crop.busy ||
          workbench.busy ||
          pointer.active ||
          layers.interacting ||
          ranges.active ||
          blocks.active ||
          effectEditor.active ||
          deformation.active ||
          merge.busy
        )
          throw new Error("Закройте настройки и добавьте изображение ещё раз.");
        return current();
      },
    );
    pointer.setTool("select");
    message(
      entries.length === 1
        ? "Изображение добавлено."
        : `Добавлено изображений: ${entries.length}.`,
    );
    schedule();
  },
  () => {
    if (
      effectEditor.active ||
      deformation.active ||
      merge.busy ||
      pointer.active ||
      session.gesturing ||
      crop.busy ||
      workbench.busy ||
      pageInspector.dirty
    )
      return false;
    workbench.finish();
    chrome.closePopup();
    return true;
  },
  message,
);
const blocks = new NativeBlocks(
  session,
  schedule,
  () => {
    if (
      effectEditor.active ||
      deformation.active ||
      merge.busy ||
      pointer.active ||
      session.gesturing ||
      crop.busy ||
      workbench.busy ||
      pageInspector.dirty
    )
      return false;
    workbench.finish();
    chrome.closePopup();
    return true;
  },
  () => {
    const area = chrome.workArea();
    return {
      x: (area.x + area.width / 2 - camera.x) / camera.zoom,
      y: (area.y + area.height / 2 - camera.y) / camera.zoom,
    };
  },
  () => pointer.setTool("select"),
  message,
  () => void imageLibrary.open(),
);
byId<HTMLInputElement>("native-image-file").onchange = () => {
  const input = byId<HTMLInputElement>("native-image-file"),
    files = Array.from(input.files ?? []);
  input.value = "";
  if (files.length) void imageLibrary.upload(files);
};
window.addEventListener("keydown", (event) => {
  if (
    chrome.modal ||
    effectEditor.active ||
    deformation.active ||
    merge.busy ||
    pageInspector.dirty ||
    crop.busy ||
    crop.gesturing ||
    workbench.active ||
    workbench.busy
  )
    return;
  if (layers.dragging && layers.keydown(event)) return;
  if (shapeTools.opened) {
    if (event.key === "Escape") shapeTools.close();
    if (!["Tab", "Enter", " "].includes(event.key)) event.preventDefault();
    return;
  }
  if (event.key === "Escape" && (shapeTools.close() || chrome.closePopup())) {
    event.preventDefault();
    return;
  }
  if (
    (event.target as HTMLElement).closest(
      "input,textarea,select,[contenteditable=true]",
    )
  )
    return;
  if (layers.keydown(event)) return;
  const command = event.ctrlKey || event.metaKey;
  if (
    event.code === "Space" &&
    !command &&
    !(event.target as HTMLElement).closest("button,a")
  ) {
    event.preventDefault();
    pointer.space = true;
    schedule();
  }
  if (event.key === "Escape") {
    if (pointer.active) pointer.cancel();
    else {
      pointer.setTool("select");
      session.selectedId = null;
      schedule();
    }
  }
  if (command && !event.altKey && ["KeyZ", "KeyY"].includes(event.code)) {
    event.preventDefault();
    if (pointer.active) pointer.cancel();
    else undo(event.code === "KeyY" || event.shiftKey);
    schedule();
    return;
  }
  if (pointer.active) return;
  if (command && !event.altKey && event.code === "KeyA") {
    event.preventDefault();
    pointer.setTool("select");
    session.select(session.page.nodes.map((n) => n.id));
    schedule();
    return;
  }
  if (event.key === "Delete") {
    event.preventDefault();
    session.removeSelected();
    schedule();
  }
  if (
    session.selectedNodes.length > 0 &&
    ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key) &&
    !command &&
    !event.altKey
  ) {
    event.preventDefault();
    const nodes = transformNodes(
      session.selectedNodes,
      nudgeCommandForKey(event.key, event.shiftKey)!,
    );
    session.replaceMany(nodes);
    session.finish();
    schedule();
  }
});
window.addEventListener("keyup", (event) => {
  if (event.code === "Space") {
    pointer.space = false;
    schedule();
  }
});
window.addEventListener("beforeunload", (event) => {
  if (
    session.dirty ||
    savingProject ||
    pointer.active ||
    layers.interacting ||
    ranges.active ||
    blocks.active ||
    crop.dirty ||
    crop.busy
  ) {
    event.preventDefault();
    event.returnValue = "";
  }
});
new ResizeObserver(() => schedule()).observe(stage);
document.fonts.addEventListener("loadingdone", () => {
  fontRevision++;
  schedule();
});
installNativeToolbarEntrance();
fit();
// Read-only diagnostics for regression and release verification; no renderer object leaks into the scene.
Object.defineProperty(window, "CreatorNative", {
  value: Object.freeze({
    version: 80,
    snapshot: () => structuredClone(session.document),
    resources: () => ({
      ...images.levels.stats(),
      pixels: images.pixels.stats(),
      sources: images.stats(),
      text: { ...textCacheStats(), ...textLayoutStats() },
      worker: images.worker.stats(),
      displayWork: images.displayWork.stats(),
      draws: scenePainter.draws,
      skipped: scenePainter.skipped,
    }),
    state: () => ({
      workTool: workbench.tool,
      region: workbench.regions.region,
      textEditing: workbench.text.editing,
      crop: crop.box
        ? { ...crop.box, source: crop.source, busy: crop.busy }
        : null,
      renderer: renderer.kind,
      selectedId: session.selectedId,
      selectedIds: session.selectedIds.slice(),
      frame: pointer.frame,
      dirty: session.dirty,
      history: session.history.length,
      redo: session.history.canRedo,
      camera: { ...camera },
      bounds: session.selected ? bounds(session.selected) : null,
    }),
  }),
});
